import { useCallback, useContext, useMemo } from 'react';
import { useRouter } from 'next/router';
import { ConfigContext } from 'services/config/ConfigProvider';
import { isBrowser, isLocalDev } from 'utils/dom';

const isUrlAbsolute = (url: string) => url?.includes('://');

export default function useEnvUrl() {
  const {
    query: { tenant },
    basePath: nextBasePath,
  } = useRouter();
  const config = useContext(ConfigContext);

  const { baseOrigin, basePath } = useMemo(
    () => ({
      baseOrigin: (isBrowser ? document.location.origin : config?.primaryDomain) || null,
      basePath: (isLocalDev && [nextBasePath || '', tenant].join('/')) || '',
    }),
    [config, nextBasePath, tenant],
  );

  const getUrl = useCallback(
    (path: string, origin: string | null | { tenant: string } = baseOrigin) => {
      const placeholder = 'test://';
      const hasValidUrl = path !== '/' || basePath !== path;

      if (path.startsWith('mailto:')) return path;

      origin =
        (origin && typeof origin === 'object' ? `${placeholder}${nextBasePath}/${origin.tenant}/` : origin) ||
        placeholder;

      if (path.startsWith('/')) path = path.substring(1);
      if (path.endsWith('/index') || path === 'index') path = path.substring(0, -5);

      let url: string = path;
      switch (true) {
        case isUrlAbsolute(path) || !hasValidUrl:
          return path;
        case isLocalDev:
          url = new URL(`${basePath}${basePath.endsWith('/') ? '' : '/'}${path}`, origin).href;
          break;
        default:
          url = new URL(path, origin).href;
          break;
      }

      if (url.startsWith(placeholder)) url = url.substring(placeholder.length);

      return decodeURI(url);
    },
    [basePath, baseOrigin, nextBasePath],
  );

  return getUrl;
}
